import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Maximize2, Loader2, Clock, Pause, CheckCircle, User,
} from 'lucide-react';
import { apiRequest, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const TILES = [
  { key: 'pending',   label: 'Pending',   Icon: Clock,       cls: 'bg-amber-50 text-amber-700' },
  { key: 'on_hold',   label: 'On Hold',   Icon: Pause,       cls: 'bg-orange-50 text-orange-700' },
  { key: 'completed', label: 'Completed', Icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700' },
];

export default function TasksIAssignedWidget({ expandable = true }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ pending: 0, on_hold: 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [list, statsData] = await Promise.all([
        apiRequest('/task-management/tasks?created_by_me=true&status=pending&limit=5'),
        apiRequest('/task-management/tasks/stats'),
      ]);
      setTasks(list?.tasks || []);
      const me = statsData?.assigned_by_me || {};
      setStats({
        pending: me.pending || 0,
        on_hold: me.on_hold || 0,
        completed: me.completed || 0,
      });
    } catch (err) {
      console.error('TasksIAssignedWidget fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 120000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const goToTasks = () => navigate('/task-management');

  return (
    <Card data-testid="tasks-i-assigned-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-teal-500" />
            <CardTitle className="text-lg">Tasks I Assigned</CardTitle>
            {stats.pending > 0 && <Badge variant="secondary">{stats.pending}</Badge>}
          </div>
          {expandable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={goToTasks}
              className="h-8 w-8"
              title="View all assigned tasks"
              data-testid="tasks-i-assigned-expand-btn"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {TILES.map(({ key, label, Icon, cls }) => (
                <button
                  key={key}
                  type="button"
                  onClick={goToTasks}
                  className={`text-center p-2 rounded-lg ${cls} hover:opacity-90 transition-opacity`}
                  data-testid={`tasks-i-assigned-tile-${key}`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Icon className="h-3.5 w-3.5" />
                    <p className="text-lg font-bold leading-none" data-testid={`tasks-i-assigned-tile-${key}-value`}>
                      {stats[key]}
                    </p>
                  </div>
                  <p className="text-[10px] mt-1">{label}</p>
                </button>
              ))}
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-4 text-slate-400">
                <Send className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No pending assigned tasks</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={() => navigate('/task-management/new')}
                  data-testid="tasks-i-assigned-create-btn"
                >
                  Assign a new task
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                  return (
                    <div
                      key={task.id}
                      className={`p-2.5 rounded-lg border cursor-pointer hover:border-slate-300 transition-colors ${
                        isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                      }`}
                      onClick={() => navigate(`/task-management/${task.id}`)}
                      data-testid={`assigned-task-item-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 line-clamp-1">{task.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <User className="h-3 w-3" />
                              {task.assigned_to_name || 'Unassigned'}
                            </span>
                            {task.due_date && (
                              <span className={`flex items-center gap-1 text-xs ${
                                isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'
                              }`}>
                                <Clock className="h-3 w-3" />
                                {formatDate(task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {stats.pending > tasks.length && (
                  <Button
                    variant="link"
                    size="sm"
                    className="w-full text-xs"
                    onClick={goToTasks}
                    data-testid="tasks-i-assigned-view-all-btn"
                  >
                    View all {stats.pending} pending assigned tasks
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
